import { clsx, type ClassValue } from 'clsx';
import { extendTailwindMerge } from 'tailwind-merge';

/* ============================================================
   클래스 합치기 — 우리 자를 tailwind-merge 에 알려 준다
   ------------------------------------------------------------
   **이걸 안 하면 두 가지가 조용히 망가진다.** 둘 다 오류도 경고도 안 나고
   화면을 띄워야만 보인다.

   **① 글자색이 사라진다.**
   단추가 `text-primary-foreground`(흰 글자) 와 `text-body`(14px) 를 함께
   달았는데, 기본 규칙은 **둘 다 `text-*` 라서 충돌**로 보고 뒤엣것만 남겼다.
   `text-body` 가 우리가 새로 만든 이름이라 글자 크기 목록에 없어서 색으로
   오인한 것이다. 결과는 파란 바탕에 남색 글자였다.

   **② 간격이 안 걷혀 승자가 빌드 순서에 달린다.**
   shadcn 컴포넌트는 `py-6` 을 달고 오고 우리는 `py-s7` 로 덮으려 한다.
   그런데 `s7` 이 기본 간격 목록에 없어 **둘 다 살아남는다** — 어느 쪽이
   이기는지는 우리가 정한 것이 아니라 생성된 CSS 의 줄 순서가 정한다.
   실측(2026-08-25): `py-6 py-s7` → 확장 전 `"py-6 py-s7"`, 확장 후 `"py-s7"`.

   `theme.spacing` 하나를 넓히면 `p-`·`m-`·`gap-`·`w-`·`h-`·`size-`·`space-`
   가 **전부** 함께 풀린다. 그룹마다 따로 적을 필요가 없다.

   ⚠ **`--spacing-*` 나 `--text-*` 를 늘리면 아래 목록도 늘려라.**
   안 늘리면 그 이름을 쓴 자리마다 위 두 증상이 되돌아온다.
   ============================================================ */

/** 간격 여덟 단계 — `styles.css` 의 `--s1`~`--s8` 과 같은 목록이다. */
const SPACING = ['s1', 's2', 's3', 's4', 's5', 's6', 's7', 's8'];

/** 글자 여섯 단계 — `styles.css` 의 `--t-micro`~`--t-h1` 과 같은 목록이다. */
const TEXT = ['micro', 'small', 'meta', 'body', 'lead', 'h1'];

/** 그림자는 하나뿐이다. 등록을 빠뜨리면 `shadow-panel` 이 `shadow-sm` 을
    **못 지운다** — 기본 규칙이 우리 이름을 그림자 *색*으로 오인한다. */
const SHADOW = ['panel'];

const twMerge = extendTailwindMerge({
  extend: {
    theme: { spacing: SPACING, text: TEXT, shadow: SHADOW },
    // `theme.text` 만으로는 색과 크기를 못 가른다. 크기 그룹에 못박아야
    // `text-white text-body` 가 둘 다 살아남는다.
    classGroups: { 'font-size': [{ text: TEXT }] },
  },
});

/** shadcn/ui 가 생성하는 모든 컴포넌트가 쓴다. 클래스 충돌을 뒤엣것 우선으로 푼다. */
export function cn(...inputs: ClassValue[]) {
  return twMerge(clsx(inputs));
}
